import type { Concept } from '@yuan-math/curriculum';

/**
 * The upper middle-school slice — what a Grade 7 syllabus contains, and what
 * `course-prealgebra` is assembled from.
 *
 * Grade 6 widened *number*. This band widens *reasoning*. Three things happen:
 *
 *   - arithmetic closes: every rational number, of either sign, obeys one set
 *     of rules, so there is no longer a subtraction you cannot do,
 *   - proportion becomes an operator rather than a comparison — scale a recipe,
 *     a map, a price, a population, all with the same move, and
 *   - an equation stops being a puzzle with one hidden step and becomes a
 *     sentence you rewrite until it says what you want.
 *
 * The geometry here is the last of the measurement work before proof arrives:
 * angles get relationships instead of just sizes, the circle introduces the
 * first constant a student meets that is not a whole number, and a scale
 * drawing turns proportion back into a picture.
 *
 * Two strands begin here that Grade 8 will take over. `proportional-relationships`
 * names the constant k in y = kx, which is the same number Grade 8 will call the
 * slope; `sampling` is the first time a student is asked to say something about
 * data they do not have.
 *
 * As everywhere, `levels` records where a concept is *typically* taught and
 * gates nothing. `prerequisites` is the real structure.
 */
export const UPPER_MIDDLE_CONCEPTS: Concept[] = [
  /* -------------------------------------------------------------- number */
  {
    id: 'rational-numbers',
    title: 'Operations with Rational Numbers',
    summary:
      'Fractions, decimals and negatives are one system, not three — and the sign rules are the same for all of them.',
    strand: 'numbers',
    levels: ['grade-7'],
    prerequisites: ['integers', 'add-fractions', 'add-decimals'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------ proportional reasoning */
  {
    id: 'proportions',
    title: 'Proportions',
    summary: 'Two ratios set equal — the single move behind scaling recipes, maps, models and prices.',
    strand: 'numbers',
    levels: ['grade-7'],
    prerequisites: ['ratios', 'unit-rates'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'proportional-relationships',
    title: 'Proportional Relationships',
    summary:
      'One number that describes a whole relationship — and the three places it hides: in a table, in an equation, in the slope of a line through the origin.',
    strand: 'algebra',
    levels: ['grade-7'],
    prerequisites: ['proportions', 'coordinate-plane'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'percent-applications',
    title: 'Percent Change and Interest',
    summary:
      'Increase, decrease, tax, tip, discount and interest — all one calculation, once you know which amount is the whole.',
    strand: 'numbers',
    levels: ['grade-7'],
    prerequisites: ['percentages', 'proportions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------------------- algebra */
  {
    id: 'linear-expressions',
    title: 'Linear Expressions',
    summary:
      'Expanding, factoring and collecting — rewriting an expression into the form that answers the question, without changing what it means.',
    strand: 'algebra',
    levels: ['grade-7'],
    prerequisites: ['algebraic-expressions', 'rational-numbers'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'multi-step-equations',
    title: 'Multi-Step Equations',
    summary: 'Solving when the unknown is behind more than one operation, or appears on both sides.',
    strand: 'algebra',
    levels: ['grade-7', 'grade-8'],
    prerequisites: ['one-step-equations', 'linear-expressions', 'rational-numbers'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'inequalities',
    title: 'Inequalities',
    summary: 'Statements about a range of numbers rather than a single value — and the one rule that reverses.',
    strand: 'algebra',
    levels: ['grade-7'],
    prerequisites: ['multi-step-equations', 'one-variable-inequalities'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ------------------------------------------------------------ geometry */
  {
    id: 'angles',
    title: 'Angles and Lines',
    summary: 'Measuring turn — and the relationships that let you find an angle you were never told.',
    strand: 'geometry',
    levels: ['grade-7'],
    prerequisites: ['angle-measure'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'circles',
    title: 'Circumference and Area of a Circle',
    summary: 'Every circle has the same ratio of circumference to diameter. That ratio is π, and it is not a fraction.',
    strand: 'geometry',
    levels: ['grade-7'],
    prerequisites: ['area-rectangles', 'ratios'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'scale-drawings',
    title: 'Scale Drawings',
    summary:
      'A map, a plan or a model is a proportion drawn out — and lengths, areas and volumes do not scale by the same number.',
    strand: 'geometry',
    levels: ['grade-7'],
    prerequisites: ['proportions', 'area-rectangles'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'surface-area',
    title: 'Surface Area',
    summary: 'How much skin a solid has, as opposed to how much space it fills — an area, so the units are squared.',
    strand: 'geometry',
    levels: ['grade-6', 'grade-7', 'grade-10'],
    prerequisites: ['volume', 'area-triangles'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* --------------------------------------------------------- probability */
  {
    id: 'basic-probability',
    title: 'Introduction to Probability',
    summary: 'Measuring how likely something is on a scale from 0 to 1 — the first mathematics of what has not happened yet.',
    strand: 'probability',
    levels: ['grade-7'],
    prerequisites: ['fraction-basics', 'percentages'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'compound-probability',
    title: 'Compound Events',
    summary:
      'Two things happening rather than one. List every outcome, or multiply along the branches — but never add.',
    strand: 'probability',
    levels: ['grade-7'],
    prerequisites: ['basic-probability', 'multiply-fractions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ---------------------------------------------------------- statistics */
  {
    id: 'sampling',
    title: 'Samples and Inferences',
    summary:
      'Saying something about a population you cannot count, from a handful you can — and being honest about the uncertainty that leaves.',
    strand: 'statistics',
    levels: ['grade-7'],
    prerequisites: ['mean-median-mode', 'basic-probability'],
    status: 'available',
    estimatedMinutes: 30,
  },
];
